import Phaser from 'phaser';
import { setGameVolume, getGameVolume } from '@/audio/audio-manager';
import { playMenuNavigate, playMenuSelect, playVolumeChange } from '@/audio/sound-generator';
import { COLORS, NEON_FONTS } from '@/config/colors';

const MENU_FONT_SIZE = 22;
const TITLE_FONT_SIZE = 42;
const BG_SPEED = 60;
const BG_PACMAN_RADIUS = 10;
const BG_GHOST_RADIUS = 10;
const BG_DOT_RADIUS = 2;
const BG_DOT_SPACING = 20;
const BG_ALPHA = 0.25;
const BG_GHOST_GAP = 30;

interface MenuItem {
  readonly text: Phaser.GameObjects.Text;
  readonly label: string;
  readonly action: () => void;
}

interface BgCharacter {
  graphics: Phaser.GameObjects.Graphics;
  x: number;
  y: number;
}

const GHOST_COLORS = [
  COLORS.GHOST_BLINKY,
  COLORS.GHOST_SPEEDY,
  COLORS.GHOST_CLYDE,
  COLORS.GHOST_INKY,
] as const;

export class MenuScene extends Phaser.Scene {
  private menuItems: MenuItem[] = [];
  private selectedIndex = 0;
  private volumeText: Phaser.GameObjects.Text | null = null;
  private bgPacman: BgCharacter | null = null;
  private bgGhosts: BgCharacter[] = [];
  private bgDots: Phaser.GameObjects.Graphics | null = null;
  private bgMouthAngle = 0;
  private sceneWidth = 0;
  private sceneHeight = 0;
  private isTransitioning = false;

  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    this.menuItems = [];
    this.selectedIndex = 0;
    this.bgGhosts = [];
    this.isTransitioning = false;

    const { width, height } = this.cameras.main;
    this.sceneWidth = width;
    this.sceneHeight = height;
    const centerX = width / 2;

    // Create background animation FIRST (lowest depth)
    this.createBackgroundAnimation();

    this.createTitle(centerX, height * 0.15);
    this.createMenuOptions(centerX, height * 0.45);
    this.createVolumeControl(centerX, height * 0.82);
    this.setupKeyboard();
    this.updateSelection();
  }

  update(_time: number, delta: number): void {
    this.updateBackgroundAnimation(delta);
  }

  private createTitle(x: number, y: number): void {
    this.add.text(x, y, 'PACKMAN', {
      fontFamily: NEON_FONTS.TITLE,
      fontSize: `${TITLE_FONT_SIZE}px`,
      fontStyle: 'bold',
      color: '#FFE500',
      align: 'center',
      shadow: { offsetX: 0, offsetY: 0, color: '#FFE500', blur: 16, fill: true },
    }).setOrigin(0.5);

    this.add.text(x, y + 55, 'A Classic Arcade Game', {
      fontFamily: NEON_FONTS.BODY,
      fontSize: '16px',
      color: '#00E5FF',
      align: 'center',
      shadow: { offsetX: 0, offsetY: 0, color: '#00E5FF', blur: 8, fill: true },
    }).setOrigin(0.5);
  }

  private createMenuOptions(x: number, startY: number): void {
    const options = [
      { label: 'NEW GAME', action: () => this.startGame() },
      { label: 'LEADERBOARD', action: () => this.showLeaderboard() },
    ];

    options.forEach((option, index) => {
      const text = this.add.text(x, startY + index * 55, option.label, {
        fontFamily: NEON_FONTS.BODY,
        fontSize: `${MENU_FONT_SIZE}px`,
        fontStyle: 'bold',
        color: '#FFFFFF',
        align: 'center',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      text.on('pointerover', () => {
        this.selectedIndex = index;
        this.updateSelection();
        playMenuNavigate(this);
      });

      text.on('pointerdown', () => {
        playMenuSelect(this);
        option.action();
      });

      this.menuItems.push({ text, label: option.label, action: option.action });
    });
  }

  private createVolumeControl(x: number, y: number): void {
    const currentVolume = getGameVolume();

    this.add.text(x, y - 30, 'VOLUME', {
      fontFamily: NEON_FONTS.BODY,
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#FFFFFF',
      align: 'center',
    }).setOrigin(0.5);

    const volumePercent = Math.round(currentVolume * 100);
    this.volumeText = this.add.text(x, y, `${volumePercent}%`, {
      fontFamily: NEON_FONTS.TITLE,
      fontSize: '18px',
      color: '#00FF88',
      align: 'center',
      shadow: { offsetX: 0, offsetY: 0, color: '#00FF88', blur: 10, fill: true },
    }).setOrigin(0.5);

    this.add.text(x, y + 30, '‹  /  › to adjust', {
      fontFamily: NEON_FONTS.BODY,
      fontSize: '12px',
      color: '#667788',
      align: 'center',
    }).setOrigin(0.5);
  }

  private setupKeyboard(): void {
    if (!this.input.keyboard) {
      return;
    }

    this.input.keyboard.on('keydown-UP', () => {
      this.navigateMenu(-1);
    });

    this.input.keyboard.on('keydown-DOWN', () => {
      this.navigateMenu(1);
    });

    this.input.keyboard.on('keydown-W', () => {
      this.navigateMenu(-1);
    });

    this.input.keyboard.on('keydown-S', () => {
      this.navigateMenu(1);
    });

    this.input.keyboard.on('keydown-ENTER', () => {
      const item = this.menuItems[this.selectedIndex];
      if (item) {
        playMenuSelect(this);
        item.action();
      }
    });

    this.input.keyboard.on('keydown-SPACE', () => {
      const item = this.menuItems[this.selectedIndex];
      if (item) {
        playMenuSelect(this);
        item.action();
      }
    });

    this.input.keyboard.on('keydown-LEFT', () => {
      this.adjustVolume(-0.1);
    });

    this.input.keyboard.on('keydown-RIGHT', () => {
      this.adjustVolume(0.1);
    });
  }

  private navigateMenu(direction: number): void {
    const count = this.menuItems.length;
    if (count === 0) {
      return;
    }
    this.selectedIndex = (this.selectedIndex + direction + count) % count;
    this.updateSelection();
    playMenuNavigate(this);
  }

  private adjustVolume(delta: number): void {
    const current = getGameVolume();
    const newVolume = Math.max(0, Math.min(1, current + delta));
    setGameVolume(newVolume);

    if (this.volumeText) {
      this.volumeText.setText(`${Math.round(newVolume * 100)}%`);
    }

    playVolumeChange(this);
  }

  private updateSelection(): void {
    this.menuItems.forEach((item, index) => {
      const isSelected = index === this.selectedIndex;
      item.text.setColor(isSelected ? '#FFE500' : '#AABBCC');
      item.text.setText(isSelected ? `▸ ${item.label} ◂` : item.label);
      item.text.setShadow(
        0, 0,
        isSelected ? '#FFE500' : 'transparent',
        isSelected ? 12 : 0,
        true, true,
      );
    });
  }

  private createBackgroundAnimation(): void {
    const chaseY = this.sceneHeight * 0.65;
    const startX = -50;

    // Create dot trail (behind everything)
    this.bgDots = this.add.graphics().setDepth(-2).setAlpha(BG_ALPHA * 0.6);
    this.drawDotTrail(chaseY);

    // Create Pac-Man
    const pacGraphics = this.add.graphics().setDepth(-1).setAlpha(BG_ALPHA);
    this.bgPacman = { graphics: pacGraphics, x: startX, y: chaseY };

    // Create 4 ghosts behind Pac-Man
    for (let i = 0; i < 4; i++) {
      const ghostGraphics = this.add.graphics().setDepth(-1).setAlpha(BG_ALPHA);
      this.bgGhosts.push({
        graphics: ghostGraphics,
        x: startX - (i + 1) * BG_GHOST_GAP,
        y: chaseY,
      });
    }

    this.drawBgPacman();
    this.drawBgGhosts();
  }

  private updateBackgroundAnimation(delta: number): void {
    const speed = BG_SPEED * (delta / 1000);
    const wrapX = this.sceneWidth + 50;
    const resetX = -50 - 4 * BG_GHOST_GAP;

    // Update mouth animation
    this.bgMouthAngle += delta * 0.006;

    // Move Pac-Man
    if (this.bgPacman) {
      this.bgPacman.x += speed;
      if (this.bgPacman.x > wrapX) {
        this.bgPacman.x = resetX + 4 * BG_GHOST_GAP;
        // Reset ghosts behind pacman when wrapping
        for (let i = 0; i < this.bgGhosts.length; i++) {
          const ghost = this.bgGhosts[i];
          if (ghost) {
            ghost.x = resetX + (3 - i) * BG_GHOST_GAP;
          }
        }
      }
      this.drawBgPacman();
    }

    // Move ghosts
    for (const ghost of this.bgGhosts) {
      ghost.x += speed;
      if (ghost.x > wrapX + 4 * BG_GHOST_GAP) {
        ghost.x = resetX;
      }
    }
    this.drawBgGhosts();
  }

  private drawDotTrail(y: number): void {
    if (!this.bgDots) {
      return;
    }
    this.bgDots.clear();
    this.bgDots.fillStyle(COLORS.DOT, 1);
    for (let x = 20; x < this.sceneWidth; x += BG_DOT_SPACING) {
      this.bgDots.fillCircle(x, y, BG_DOT_RADIUS);
    }
  }

  private drawBgPacman(): void {
    if (!this.bgPacman) {
      return;
    }
    const { graphics, x, y } = this.bgPacman;
    graphics.clear();
    graphics.fillStyle(COLORS.PACMAN, 1);

    const mouthOpen = Math.sin(this.bgMouthAngle) > 0;
    if (mouthOpen) {
      const gapAngle = Math.PI / 6;
      graphics.beginPath();
      graphics.moveTo(x, y);
      graphics.arc(x, y, BG_PACMAN_RADIUS, gapAngle, Math.PI * 2 - gapAngle);
      graphics.closePath();
      graphics.fillPath();
    } else {
      graphics.fillCircle(x, y, BG_PACMAN_RADIUS);
    }
  }

  private drawBgGhosts(): void {
    for (let i = 0; i < this.bgGhosts.length; i++) {
      const ghost = this.bgGhosts[i];
      if (!ghost) {
        continue;
      }
      const color = GHOST_COLORS[i] ?? COLORS.GHOST_BLINKY;
      const { graphics, x, y } = ghost;
      graphics.clear();

      // Ghost body (dome + rectangle base)
      graphics.fillStyle(color, 1);
      graphics.fillCircle(x, y - 2, BG_GHOST_RADIUS);
      graphics.fillRect(
        x - BG_GHOST_RADIUS,
        y - 2,
        BG_GHOST_RADIUS * 2,
        BG_GHOST_RADIUS,
      );

      // Wavy bottom edge
      const baseY = y + BG_GHOST_RADIUS - 2;
      for (let w = 0; w < 3; w++) {
        const wx = x - BG_GHOST_RADIUS + w * (BG_GHOST_RADIUS * 2 / 3) + BG_GHOST_RADIUS / 3;
        graphics.fillCircle(wx, baseY, BG_GHOST_RADIUS / 3);
      }

      // Eyes (white circles with black pupils)
      graphics.fillStyle(0xffffff, 1);
      graphics.fillCircle(x - 3, y - 4, 3);
      graphics.fillCircle(x + 3, y - 4, 3);
      graphics.fillStyle(0x000000, 1);
      graphics.fillCircle(x - 2, y - 4, 1.5);
      graphics.fillCircle(x + 4, y - 4, 1.5);
    }
  }

  private startGame(): void {
    if (this.isTransitioning) {
      return;
    }
    this.isTransitioning = true;
    this.scene.start('LevelTransitionScene', {
      score: 0,
      lives: 3,
      level: 1,
      extraLifeAwarded: false,
    });
  }

  private showLeaderboard(): void {
    if (this.isTransitioning) {
      return;
    }
    this.isTransitioning = true;
    this.scene.start('LeaderboardScene');
  }
}

